import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { Button, CircularProgress, FormControlLabel, Radio, RadioGroup, TextField, Tooltip, Typography } from '@material-ui/core';
import { Alert } from '@mui/material';
import resolveExistingIdentity from '../../../repository/resolveExistingIdentity';
import LinkExistingIdentityConfirmDialog from '../../../custom-widgets/dialogs/LinkExistingIdentityConfirmDialog';

/**
 * PFM-3029: shared New User / Link Existing branch control for
 * every Express-backed user-create form. The creator checks one exact
 * username or email; the control only ever holds canonical display fields
 * plus the opaque server-issued selection token. A confirmed selection locks
 * the identifiers and the existing password remains unchanged — no password
 * is ever requested or transmitted for a linked identity.
 */
export const LINK_MODES = { NEW: 'new', LINK: 'link' };

const LINK_HIDDEN_FIELDS = ['username', 'email', 'password', 'password_confirmation'];
const LINK_NOT_REQUIRED_FIELDS = ['username', 'email'];

export const LINK_CONFIRM_BEFORE_ADD = 'Confirm linking before adding this user.';
export const LINK_EXPIRED_MESSAGE = 'This link expired or is no longer available. Please check the link again.';
export const LINK_CONFLICT_FORM_ERROR = 'A user with this username or email already exists. Confirm the link in the dialog.';
export const LINK_RATE_LIMIT_MESSAGE = 'Too many checks in a short time. Please wait a few minutes and try again.';

/** Capitalize the first letter of each word; names are usually stored lowercase. */
export const titleCaseName = (value) => (value ? value.replace(/(^|\s)(\S)/g, (match, space, letter) => `${space}${letter.toUpperCase()}`) : value);

/**
 * PFM-3029: configured (FormCreator) forms in link mode hide the username,
 * email and password fields entirely — a link never transmits any of them
 * (the server adopts the token's canonical identity and stores an unusable
 * random hash). `form_layout` is left untouched so FormCreator's index-based
 * field keys stay stable (no remount/unregister of other fields when the
 * mode toggles); FieldCreator renders nothing for a field without a Component.
 */
export function linkModeFormConfig(regionConfig, linkMode) {
    if (linkMode !== LINK_MODES.LINK || !regionConfig?.form) return regionConfig;
    const form = { ...regionConfig.form };
    LINK_NOT_REQUIRED_FIELDS.forEach((name) => {
        if (form[name]) {
            form[name] = { ...form[name], props: { ...form[name].props, required: false } };
        }
    });
    LINK_HIDDEN_FIELDS.forEach((name) => {
        if (form[name]) {
            form[name] = { ...form[name], Component: null };
        }
    });
    return { ...regionConfig, form };
}

const CONTROL_STATES = {
    NEW: 'new',
    CHECKING: 'checking',
    UNAVAILABLE: 'unavailable',
    SAME_REGION: 'same_region',
    RATE_LIMITED: 'rate_limited',
    ELIGIBLE: 'eligible',
    CONFIRMED: 'confirmed',
    ERROR: 'error',
};

const identifierSchema = yup.string().trim().min(3, 'Enter a full username or email').max(320, 'Enter a full username or email').required('Enter a full username or email');

/**
 * Merge a confirmed selection into a create submit model: ONLY the opaque
 * token is attached. DR-9 (human decision): an email lookup never reveals
 * the canonical username, so the browser cannot supply identifiers — the
 * server adopts the token's canonical username/email. Username, email,
 * password, and confirmation are all stripped so a link never transmits
 * typed credentials or a new password.
 */
export const applyLinkSelection = (model, selection) => {
    const merged = { ...model };
    const selectionToken = selection?.linkSelectionToken ?? selection?.selectionToken;
    if (!selectionToken) return merged;
    merged.linkSelectionToken = selectionToken;
    delete merged.username;
    delete merged.email;
    delete merged.password;
    delete merged.password_confirmation;
    return merged;
};

/**
 * Normalize the server 409 confirmed-link offer carried in error details.
 * Returns null for anything that is not the offer, so callers fail into
 * their normal alert path.
 */
export const extractLinkOffer = (err) => {
    const details = err?.details ?? err?.response?.data?.details ?? err?.response?.data?.error?.details;
    const statusCode = err?.statusCode ?? err?.response?.status;
    if (statusCode === 409 && details?.code === 'IDENTITY_CONFLICT' && details?.linkAvailable) {
        return details;
    }
    return null;
};

/**
 * DRY: one implementation of link state + the 409 offer round-trip for all
 * 13 create surfaces. Class components use `getClassLinkProps` with the same
 * shape; every form's `runCreate` delegates to `runLinkCreate` with only its
 * own action/messages/navigation callbacks.
 */
export const linkStateDefaults = {
    linkMode: LINK_MODES.NEW,
    linkSelection: null,
    linkPrompt: null,
};

/**
 * DR-4 (human-approved): a burned selection (410 expired/replayed, or a
 * typed grant reject) resets the form to a fresh check instead of leaving a
 * dead confirmed state.
 */
const LINK_RESET_CODES = ['IDENTITY_CHANGED', 'NOT_ELIGIBLE', 'ALREADY_TARGETED'];
export const isBurnedLinkSelection = (err) => {
    const statusCode = err?.statusCode ?? err?.response?.status;
    if (statusCode === 410) return true;
    const details = err?.details ?? err?.response?.data?.details;
    return statusCode === 409 && LINK_RESET_CODES.includes(details?.code);
};

export const runLinkCreate = ({ action, model, onSuccess, onError, onLinkOffer, onResetLink }) =>
    action(model)
        .then(onSuccess)
        .catch((err) => {
            const offer = extractLinkOffer(err);
            if (offer) {
                return onLinkOffer({ offer, model });
            }
            if (onResetLink && isBurnedLinkSelection(err)) {
                onResetLink(LINK_EXPIRED_MESSAGE);
                return undefined;
            }
            return onError(err);
        });

export const useIdentityLinkState = () => {
    const [linkMode, setLinkMode] = useState(LINK_MODES.NEW);
    const [linkSelection, setLinkSelection] = useState(null);
    const [linkPrompt, setLinkPrompt] = useState(null);
    const linkPromptRef = useRef(null);
    linkPromptRef.current = linkPrompt;
    return {
        linkMode,
        linkSelection,
        linkPrompt,
        handleLinkModeChange: (mode) => {
            setLinkMode(mode);
            setLinkSelection(null);
        },
        setLinkSelection,
        setLinkPrompt,
        // DR-8 (human-approved): read state via ref and call runCreate OUTSIDE
        // any setState updater — updaters must be pure and StrictMode can
        // double-invoke them, which could fire two creates.
        confirmLinkPrompt: (runCreate) => {
            const { current } = linkPromptRef;
            if (!current) return;
            setLinkPrompt(null);
            runCreate(applyLinkSelection(current.model, current.offer));
        },
        resetLink: (message) => {
            setLinkMode(LINK_MODES.NEW);
            setLinkSelection(null);
            setLinkPrompt(null);
            return message;
        },
        dismissLinkPrompt: () => setLinkPrompt(null),
        submitGuard: () => linkMode === LINK_MODES.LINK && !linkSelection,
    };
};

export const getClassLinkProps = (component) => ({
    linkMode: component.state.linkMode,
    linkSelection: component.state.linkSelection,
    linkPrompt: component.state.linkPrompt,
    handleLinkModeChange: (mode) => component.setState({ linkMode: mode, linkSelection: null }),
    setLinkSelection: (selection) => component.setState({ linkSelection: selection }),
    setLinkPrompt: (prompt) => component.setState({ linkPrompt: prompt }),
    confirmLinkPrompt: (runCreate) => {
        const { linkPrompt } = component.state;
        if (!linkPrompt) return;
        component.setState({ linkPrompt: null });
        runCreate(applyLinkSelection(linkPrompt.model, linkPrompt.offer));
    },
    resetLink: (message) => {
        component.setState({ linkMode: LINK_MODES.NEW, linkSelection: null, linkPrompt: null });
        return message;
    },
    dismissLinkPrompt: () => component.setState({ linkPrompt: null }),
    submitGuard: () => component.state.linkMode === LINK_MODES.LINK && !component.state.linkSelection,
});

function ExistingIdentityLinkControl({ targetRole, isSubuser, disabled, onModeChange, onLinked }) {
    const [mode, setMode] = useState(LINK_MODES.NEW);
    const [status, setStatus] = useState(CONTROL_STATES.NEW);
    const [identifier, setIdentifier] = useState('');
    const [fieldError, setFieldError] = useState(null);
    const [result, setResult] = useState(null);

    const switchMode = (event) => {
        const nextMode = event.target.value;
        setMode(nextMode);
        setStatus(CONTROL_STATES.NEW);
        setIdentifier('');
        setFieldError(null);
        setResult(null);
        onModeChange(nextMode);
        onLinked(null);
    };

    const checkIdentity = async () => {
        try {
            await identifierSchema.validate(identifier);
        } catch (validationError) {
            setFieldError(validationError.message);
            return;
        }
        setFieldError(null);
        setStatus(CONTROL_STATES.CHECKING);
        try {
            const response = await resolveExistingIdentity({ role: targetRole, subuser: isSubuser, identifier: identifier.trim() });
            if (response?.data?.outcome === 'eligible') {
                setResult(response.data);
                setStatus(CONTROL_STATES.ELIGIBLE);
            } else if (response?.data?.outcome === 'same_region') {
                setResult(null);
                setStatus(CONTROL_STATES.SAME_REGION);
            } else {
                setResult(null);
                setStatus(CONTROL_STATES.UNAVAILABLE);
            }
        } catch (error) {
            setResult(null);
            setStatus(error?.statusCode === 429 ? CONTROL_STATES.RATE_LIMITED : CONTROL_STATES.ERROR);
        }
    };

    const confirmSelection = () => {
        if (status !== CONTROL_STATES.ELIGIBLE || !result?.selectionToken) return;
        setStatus(CONTROL_STATES.CONFIRMED);
        onLinked({
            linkSelectionToken: result.selectionToken,
            identifier: result.identifier,
            email: result.email,
            displayName: result.displayName,
        });
    };

    const resetSelection = () => {
        setStatus(CONTROL_STATES.NEW);
        setResult(null);
        onLinked(null);
    };

    return (
        <div className="my-16">
            <RadioGroup row aria-label="identity mode" name="identity-mode" value={mode} onChange={switchMode}>
                <FormControlLabel value={LINK_MODES.NEW} control={<Radio />} label="New User" disabled={disabled} />
                <FormControlLabel value={LINK_MODES.LINK} control={<Radio />} label="Link Existing" disabled={disabled} />
            </RadioGroup>

            {mode === LINK_MODES.LINK && (
                <div>
                    <Typography variant="body2">Enter the exact username or email of the existing user. Their password will not change.</Typography>
                    <TextField
                        className="my-16"
                        label="Existing username or email"
                        variant="outlined"
                        fullWidth
                        value={identifier}
                        onChange={(event) => setIdentifier(event.target.value)}
                        disabled={disabled || status === CONTROL_STATES.CHECKING || status === CONTROL_STATES.CONFIRMED}
                        error={!!fieldError}
                        helperText={fieldError}
                        inputProps={{ 'data-testid': 'identity-identifier' }}
                    />
                    {status !== CONTROL_STATES.CONFIRMED && (
                        <Button variant="contained" color="secondary" onClick={checkIdentity} disabled={disabled || status === CONTROL_STATES.CHECKING}>
                            {status === CONTROL_STATES.CHECKING ? <CircularProgress size={20} /> : 'Check Link'}
                        </Button>
                    )}

                    {status === CONTROL_STATES.UNAVAILABLE && (
                        <Alert className="mt-16" severity="warning">
                            No existing user was found for that username or email.
                        </Alert>
                    )}

                    {status === CONTROL_STATES.SAME_REGION && (
                        <Alert className="mt-16" severity="warning">
                            User already exists in this region.
                        </Alert>
                    )}

                    {status === CONTROL_STATES.RATE_LIMITED && (
                        <Alert className="mt-16" severity="warning">
                            {LINK_RATE_LIMIT_MESSAGE}
                        </Alert>
                    )}

                    {status === CONTROL_STATES.ERROR && (
                        <Alert className="mt-16" severity="error">
                            The check could not be completed. Please try again shortly.
                        </Alert>
                    )}

                    {status === CONTROL_STATES.ELIGIBLE && result && (
                        <Alert className="mt-16" severity="info">
                            <div>
                                <strong>{titleCaseName(result.displayName) || result.identifier}</strong>
                                {result.email ? ` — ${result.identifier} (${result.email})` : ` — ${result.identifier}`}
                            </div>
                            <div>A user with this username or email already exists. Do you want to link them to this account? Their password will not change.</div>
                            <Button variant="contained" color="primary" className="mt-8" onClick={confirmSelection}>
                                Confirm Link
                            </Button>
                        </Alert>
                    )}

                    {status === CONTROL_STATES.CONFIRMED && result && (
                        <Alert
                            className="mt-16"
                            severity="success"
                            action={
                                <Button color="inherit" size="small" onClick={resetSelection}>
                                    Change
                                </Button>
                            }
                        >
                            {`Linking ${titleCaseName(result.displayName) || result.identifier} (${result.identifier}). Their password will not change.`}
                        </Alert>
                    )}
                </div>
            )}
        </div>
    );
}

ExistingIdentityLinkControl.propTypes = {
    targetRole: PropTypes.string.isRequired,
    isSubuser: PropTypes.bool,
    disabled: PropTypes.bool,
    onModeChange: PropTypes.func.isRequired,
    onLinked: PropTypes.func.isRequired,
};

ExistingIdentityLinkControl.defaultProps = {
    isSubuser: false,
    disabled: false,
};

export default ExistingIdentityLinkControl;

/**
 * PFM-3029: submit button that is disabled, with an explanatory tooltip on
 * hover and on click, while a link is not yet confirmed. A disabled MUI
 * button swallows pointer events, so the tooltip lives on a wrapping span
 * that carries the layout className.
 */
export function LinkGuardedButton({ blocked, className, children, ...buttonProps }) {
    const [open, setOpen] = useState(false);
    if (!blocked) {
        return (
            <Button className={className} {...buttonProps}>
                {children}
            </Button>
        );
    }
    return (
        <Tooltip title={LINK_CONFIRM_BEFORE_ADD} open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)}>
            <span className={className} style={{ display: 'inline-block' }} onClick={() => setOpen(true)} role="presentation">
                <Button {...buttonProps} disabled>
                    {children}
                </Button>
            </span>
        </Tooltip>
    );
}

LinkGuardedButton.propTypes = {
    blocked: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
};

LinkGuardedButton.defaultProps = {
    blocked: false,
    className: undefined,
    children: null,
};

/**
 * DRY composite: the confirm dialog + the branch control rendered once by
 * every create form. `link` is the object from `useIdentityLinkState` or
 * `getClassLinkProps`; `onConfirmLink` runs the offer round-trip
 * (`link.confirmLinkPrompt(runCreate)`).
 */
export function ExistingIdentityLinkSection({ targetRole, isSubuser, disabled, show, link, onConfirmLink }) {
    return (
        <>
            <LinkExistingIdentityConfirmDialog open={!!link.linkPrompt} identity={link.linkPrompt?.offer} onConfirm={onConfirmLink} onCancel={link.dismissLinkPrompt} />
            {show && <ExistingIdentityLinkControl targetRole={targetRole} isSubuser={isSubuser} disabled={disabled} onModeChange={link.handleLinkModeChange} onLinked={link.setLinkSelection} />}
        </>
    );
}

ExistingIdentityLinkSection.propTypes = {
    targetRole: PropTypes.string.isRequired,
    isSubuser: PropTypes.bool,
    disabled: PropTypes.bool,
    show: PropTypes.bool,
    link: PropTypes.shape({
        linkMode: PropTypes.string,
        linkSelection: PropTypes.shape({}),
        linkPrompt: PropTypes.shape({ offer: PropTypes.shape({}), model: PropTypes.shape({}) }),
        handleLinkModeChange: PropTypes.func.isRequired,
        setLinkSelection: PropTypes.func.isRequired,
        dismissLinkPrompt: PropTypes.func.isRequired,
    }).isRequired,
    onConfirmLink: PropTypes.func.isRequired,
};

ExistingIdentityLinkSection.defaultProps = {
    isSubuser: false,
    disabled: false,
    show: true,
};
